import { i18n } from '@i18n/index'
import { useLanguage } from '@i18n/useLanguage'
import { useCallback, useState } from 'react'
import { Button } from '@app/components/core/button/Button'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { EditItem } from '../shared/EditItem'
import { SearchSelectOption } from '@app/components/core/input/searchSelect/SearchSelect'
import { AmountPicker } from '@app/components/core/input/amountPicker/AmountPicker'
import { Select } from '@app/components/core/input/select/Select'
import { LocalitySelector } from '@app/components/blocks/smartInputs/LocalitySelector'
import { EditButton } from '@app/components/core/button/EditButton'
import { useQuery } from '@based/sdk/react'
import { PersonalProfileModel } from 'functions/app/dashboard/profile/view'

export const AboutInfoEditPanel = ({
  profile,
  onSave,
}: {
  profile: PersonalProfileModel
  onSave: (
    ageCategory: number | null,
    residenceId: number,
    numberOfAdults: number,
    numberOfChildren: number,
  ) => Promise<boolean>
}) => {
  const [language, _setLanguage] = useLanguage()
  const translations = i18n.languages[language].app.dashboard.profile
  const commonTranslations = i18n.languages[language].app.common
  const [editMode, setEditMode] = useState(false)
  const [ageCategory, setAgeCategory] = useState(profile.ageCategory)
  const ageCategoriesQuery = useQuery('list-age-categories')
  const ageCategoriesOptions: { id: number; name: string }[] =
    ageCategoriesQuery.data ?? []

  const [residence, setResidence] = useState<SearchSelectOption>({
    key: profile.location.id,
    value: profile.location.name,
  })
  const [numberOfAdults, setNumberOfAdults] = useState(profile.numberOfAdults)
  const [numberOfChildren, setNumberOfChildren] = useState(
    profile.numberOfChildren,
  )

  const reset = useCallback(() => {
    setAgeCategory(profile.ageCategory)
    setResidence({
      key: profile.location.id,
      value: profile.location.name,
    })
    setNumberOfAdults(profile.numberOfAdults)
    setNumberOfChildren(profile.numberOfChildren)
    setEditMode(false)
  }, [profile])

  const save = useCallback(() => {
    void onSave(
      ageCategory?.id,
      residence.key,
      numberOfAdults,
      numberOfChildren,
    ).then((success) => {
      if (success) {
        setEditMode(false)
      }
    })
  }, [ageCategory, residence, numberOfAdults, numberOfChildren, onSave])

  return (
    <Column
      alignItems="stretch"
      style={{
        backgroundColor: 'var(--shades-white)',
        padding: '1.5rem',
        borderRadius: '1.25rem',
      }}
    >
      <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title.H4 style={{ marginBottom: '0' }}>{translations.about}</Title.H4>
        <EditButton isVisible={!editMode} onClick={() => setEditMode(true)} />
      </Row>
      <Column gap={editMode ? '2rem' : '1rem'} alignItems="stretch">
        <EditItem
          label={translations.ageCategory}
          editMode={editMode}
          displayValue={profile.ageCategory ? profile.ageCategory.name : ''}
        >
          <Select
            label={translations.ageCategory}
            value={ageCategory?.id.toString()}
            setValue={(value) =>
              setAgeCategory(value ? { id: Number(value), name: '' } : null)
            }
            options={ageCategoriesOptions.map((category) => ({
              value: category.id.toString(),
              label: category.name,
            }))}
          />
        </EditItem>
        <EditItem
          label={translations.location}
          editMode={editMode}
          displayValue={residence.value}
        >
          <LocalitySelector
            label={translations.location}
            locality={residence}
            setLocality={setResidence}
          />
        </EditItem>
        <EditItem
          label={translations.numberOfAdults}
          editMode={editMode}
          displayValue={numberOfAdults}
        >
          <AmountPicker
            label={translations.numberOfAdults}
            value={numberOfAdults}
            setValue={setNumberOfAdults}
            min={1}
          />
        </EditItem>
        <EditItem
          label={translations.numberOfChildren}
          editMode={editMode}
          displayValue={numberOfChildren}
        >
          <AmountPicker
            label={translations.numberOfChildren}
            value={numberOfChildren}
            setValue={setNumberOfChildren}
          />
        </EditItem>
      </Column>
      {editMode && (
        <Row style={{ justifyContent: 'flex-end', paddingTop: '1rem' }}>
          <Button
            variant="secondary"
            label={commonTranslations.cancel}
            onClick={reset}
          />
          <Button
            variant="primary"
            label={commonTranslations.save}
            onClick={save}
          />
        </Row>
      )}
    </Column>
  )
}
